import React, { useState, useEffect, useRef } from 'react';

const API_URL = window.location.origin;

interface Camera {
  id: string;
  name: string;
  created_at: string;
  last_seen: string;
}

interface Recording {
  id: string;
  camera_id: string;
  camera_name: string;
  start_time: string;
  s3_key?: string;
}

function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('lapcam_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
  const [viewingMode, setViewingMode] = useState<'live' | 'playback'>('live');
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        localStorage.setItem('lapcam_token', data.token);
      } else {
        alert('Login failed');
      }
    } catch (err) {
      alert('Login failed');
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('lapcam_token');
    disconnectStream();
  };

  const fetchCameras = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/cameras`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCameras(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRecordings = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/recordings?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecordings(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchCameras();
      fetchRecordings();
    }
  }, [token]);

  const fetchIceServers = async (): Promise<RTCIceServer[]> => {
    try {
      const res = await fetch(`${API_URL}/api/ice-servers`);
      if (res.ok) {
        const data = await res.json();
        return data.iceServers || [];
      }
    } catch (err) {
      console.error('Failed to fetch ICE servers:', err);
    }
    // Fallback to public STUN servers
    return [
      { urls: ['stun:stun.l.google.com:19302'] },
      { urls: ['stun:stun1.l.google.com:19302'] }
    ];
  };

  const connectToStream = async (cameraId: string) => {
    disconnectStream();

    if (!token) {
      alert('Please login first');
      return;
    }

    try {
      // Fetch ICE servers configuration
      const iceServers = await fetchIceServers();
      console.log('Using ICE servers:', iceServers.length, 'configurations');
      
      const pc = new RTCPeerConnection({
        iceServers
      });
      pcRef.current = pc;

      pc.ontrack = (event) => {
        console.log('✓ Received track:', event.track.kind);
        if (videoRef.current && event.streams[0]) {
          videoRef.current.srcObject = event.streams[0];
          console.log('Playing video...');
          videoRef.current.play().catch(e => console.error('Play error:', e));
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('ICE state:', pc.iceConnectionState);
      };

      pc.onconnectionstatechange = () => {
        console.log('Connection state:', pc.connectionState);
      };

      // Connect to viewer WebSocket FIRST before updating UI
      const wsUrl = `wss://${window.location.host}/ws/viewer?token=${encodeURIComponent(token!)}`;
      console.log('Connecting to:', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      console.log('WebSocket created, readyState:', ws.readyState);

      ws.onopen = async () => {
        console.log('🔵 STEP 0: WS onopen FIRED');
        console.log('🔵 readyState:', ws.readyState, '(OPEN=', WebSocket.OPEN, ')');
        
        try {
          console.log('🔵 STEP 1: Creating offer...');
          const offer = await pc.createOffer();
          console.log('🔵 STEP 2: Offer created, has sdp:', !!offer.sdp);
          
          console.log('🔵 STEP 3: Setting local description...');
          await pc.setLocalDescription(offer);
          console.log('🔵 STEP 4: Local description SET');
          
          const finalSdp = pc.localDescription?.sdp;
          console.log('🔵 STEP 5: SDP length:', finalSdp?.length || 0);
          
          if (!finalSdp) {
            console.error('❌ ERROR: No SDP!');
            alert('No SDP generated');
            return;
          }
          
          // Send watch request
          console.log('🔵 STEP 6: Sending watch message (', finalSdp.length, 'bytes)...');
          const watchMsg = JSON.stringify({
            type: 'watch',
            cameraId,
            sdp: finalSdp
          });
          console.log('🔵 WebSocket readyState before send:', ws.readyState);
          ws.send(watchMsg);
          console.log('🔵 STEP 7: Watch message SENT! ✓');
          
          // Update UI
          console.log('🔵 STEP 8: Updating UI...');
          setSelectedCamera(cameraId);
          setViewingMode('live');
          console.log('🟢 COMPLETE - stream should be playing!');
        } catch (err) {
          console.error('❌ ERROR in onopen:', err);
          alert(`Failed: ${err}`);
        }
      };
      
      ws.onerror = (err) => {
        console.error('❌ WS error:', err);
      };
      
      ws.onclose = (e) => {
        console.log('🔴 WS closed: code=', e.code, 'reason=', e.reason);
      };

      ws.onmessage = async (event) => {
        const msg = JSON.parse(event.data);
        console.log('Viewer WS message:', msg.type);

        if (msg.type === 'answer') {
          console.log('Got answer, setting remote description...');
          const answer = new RTCSessionDescription({
            type: 'answer',
            sdp: msg.sdp
          });
          await pc.setRemoteDescription(answer);
          console.log('✓ Remote description set, waiting for video...');
        } else if (msg.type === 'error') {
          console.error('Server error:', msg.message);
          alert(`Error: ${msg.message}`);
        }
      };

      ws.onerror = (err) => {
        console.error('WS error:', err);
      };

      ws.onclose = () => {
        console.log('WS closed');
      };

    } catch (err) {
      console.error('Failed to connect:', err);
      alert('Failed to connect to stream');
    }
  };

  const playRecording = async (recording: Recording) => {
    setSelectedCamera(recording.camera_id);
    setViewingMode('playback');
    disconnectStream();

    try {
      const res = await fetch(`${API_URL}/api/recordings/${recording.id}/url`, {
        headers: { Authorization: `Bearer ${token!}` }
      });
      if (res.ok) {
        const { url } = await res.json();
        if (videoRef.current) {
          videoRef.current.src = url;
          videoRef.current.play();
        }
      }
    } catch (err) {
      console.error('Failed to load recording:', err);
    }
  };

  const disconnectStream = () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.src = '';
    }
    setSelectedCamera(null);
  };

  if (!token) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>LapCam</h1>
        <form onSubmit={handleLogin} style={styles.loginForm}>
          <input type="text" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} style={styles.input} />
          <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={styles.input} />
          <button type="submit" style={styles.button}>Login</button>
        </form>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>LapCam</h1>
        <button onClick={handleLogout} style={styles.logoutButton}>Logout</button>
      </header>

      <div style={styles.main}>
        <div style={styles.sidebar}>
          <h2>Cameras ({cameras.length})</h2>
          <ul style={styles.list}>
            {cameras.map((cam) => (
              <li key={cam.id} style={styles.listItem}>
                <div>
                  <strong>{cam.name}</strong>
                  <br />
                  <small style={{color: cam.last_seen ? '#28a745' : '#dc3545'}}>
                    {cam.last_seen ? '● Online' : '○ Offline'}
                  </small>
                </div>
                <button onClick={() => connectToStream(cam.id)} style={styles.smallButton}>Watch Live</button>
              </li>
            ))}
          </ul>

          <h2>Recordings ({recordings.length})</h2>
          <ul style={styles.list}>
            {recordings.map((rec) => (
              <li key={rec.id} style={styles.listItem}>
                <div>
                  <strong>{rec.camera_name}</strong>
                  <br />
                  <small>{new Date(rec.start_time).toLocaleString()}</small>
                </div>
                <button onClick={() => playRecording(rec)} style={styles.smallButton}>Play</button>
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.content}>
          {selectedCamera ? (
            <>
              <div style={styles.videoContainer}>
                <video ref={videoRef} autoPlay playsInline controls={viewingMode === 'playback'} style={styles.video} />
              </div>
              <div style={styles.videoInfo}>
                <h3>{viewingMode === 'live' ? 'Live View' : 'Playback'} - {cameras.find(c => c.id === selectedCamera)?.name || ''}</h3>
                <button onClick={disconnectStream} style={styles.button}>Disconnect</button>
              </div>
            </>
          ) : (
            <div style={styles.placeholder}>Select a camera to view</div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minHeight: '100vh', backgroundColor: '#f5f5f5' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', backgroundColor: '#fff', borderBottom: '1px solid #ddd' },
  title: { margin: 0, fontSize: 24, color: '#333' },
  loginForm: { display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 300, margin: '100px auto', padding: 24, backgroundColor: '#fff', borderRadius: 8 },
  input: { padding: '10px 12px', fontSize: 16, border: '1px solid #ddd', borderRadius: 4 },
  button: { padding: '10px 20px', fontSize: 16, backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  logoutButton: { padding: '8px 16px', fontSize: 14, backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  main: { display: 'flex', height: 'calc(100vh - 73px)' },
  sidebar: { width: 300, padding: 16, backgroundColor: '#fff', borderRight: '1px solid #ddd', overflowY: 'auto' },
  list: { listStyle: 'none', padding: 0, margin: '0 0 20px 0' },
  listItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, marginBottom: 8, backgroundColor: '#fafafa', borderRadius: 4 },
  smallButton: { padding: '6px 12px', fontSize: 12, backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' },
  content: { flex: 1, padding: 24, overflowY: 'auto' },
  videoContainer: { backgroundColor: '#000', borderRadius: 8, overflow: 'hidden', marginBottom: 16 },
  video: { width: '100%', maxHeight: '70vh', display: 'block' },
  videoInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  placeholder: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999', fontSize: 18 }
};

export default App;
